import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pencil, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";

interface SecurityProperty {
  id: string;
  securityId: string;
  isResidential: boolean;
  addressUnavailable: boolean | null;
  addressLine1: string | null;
  addressLine2: string | null;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
  country: string | null;
}

interface PropertyAddressSectionProps {
  securityId: string;
  property: SecurityProperty | null;
  isPropertyLoading?: boolean;
}

// Australian states for dropdown
const AUSTRALIAN_STATES = ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"];

// Postcode validation regex (4 digits)
const POSTCODE_REGEX = /^\d{4}$/;

export function PropertyAddressSection({
  securityId,
  property,
  isPropertyLoading = false,
}: PropertyAddressSectionProps) {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);

  // Form state
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [suburb, setSuburb] = useState("");
  const [state, setState] = useState("");
  const [postcode, setPostcode] = useState("");
  const [addressUnavailable, setAddressUnavailable] = useState(false);
  const [postcodeError, setPostcodeError] = useState<string | null>(null);

  // Determine if property extension exists (for POST vs PATCH)
  const propertyExists = !!property?.id;

  // Initialize form with existing data when entering edit mode
  const enterEditMode = () => {
    if (property) {
      setAddressLine1(property.addressLine1 || "");
      setAddressLine2(property.addressLine2 || "");
      setSuburb(property.suburb || "");
      setState(property.state || "");
      setPostcode(property.postcode || "");
      setAddressUnavailable(property.addressUnavailable || false);
    } else {
      // Reset to empty for new address
      setAddressLine1("");
      setAddressLine2("");
      setSuburb("");
      setState("");
      setPostcode("");
      setAddressUnavailable(false);
    }
    setPostcodeError(null);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setPostcodeError(null);
  };

  // Save mutation - handles both POST (new) and PATCH (update)
  const saveMutation = useMutation({
    mutationFn: async (data: Partial<SecurityProperty>) => {
      const method = propertyExists ? "PATCH" : "POST";
      const response = await apiRequest(
        method,
        `/api/crm/securities/${securityId}/property`,
        data
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to update property address");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/crm/securities/${securityId}/property`],
      });
      toast({
        title: "Success",
        description: "Property address updated",
      });
      setIsEditing(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      // Keep editing mode open so user can retry
    },
  });

  const handleSave = () => {
    // Validate postcode if not address unavailable
    if (!addressUnavailable && postcode && !POSTCODE_REGEX.test(postcode)) {
      setPostcodeError("Postcode must be 4 digits");
      return;
    }

    setPostcodeError(null);

    const data: Partial<SecurityProperty> = {
      addressUnavailable,
    };

    // Only include address fields if address is available
    if (!addressUnavailable) {
      data.addressLine1 = addressLine1 || null;
      data.addressLine2 = addressLine2 || null;
      data.suburb = suburb || null;
      data.state = state || null;
      data.postcode = postcode || null;
    } else {
      // Clear address fields when marking unavailable
      data.addressLine1 = null;
      data.addressLine2 = null;
      data.suburb = null;
      data.state = null;
      data.postcode = null;
    }

    saveMutation.mutate(data);
  };

  // Format address for display
  const formatAddress = () => {
    if (!property) return null;

    const parts = [];
    if (property.addressLine1) parts.push(property.addressLine1);
    if (property.addressLine2) parts.push(property.addressLine2);

    const cityStateParts = [];
    if (property.suburb) cityStateParts.push(property.suburb);
    if (property.state) cityStateParts.push(property.state);
    if (property.postcode) cityStateParts.push(property.postcode);

    if (cityStateParts.length > 0) {
      parts.push(cityStateParts.join(" "));
    }

    return parts.length > 0 ? parts.join(", ") : null;
  };

  // Check if address is available (has address data)
  const hasAddress =
    property &&
    !property.addressUnavailable &&
    (property.addressLine1 || property.suburb || property.state);

  // Determine display state
  const showAddressUnavailable = property?.addressUnavailable === true;
  const showNoAddress = !property || (!hasAddress && !showAddressUnavailable);
  const showAddress = hasAddress && !showAddressUnavailable;

  if (isPropertyLoading) {
    return (
      <Card data-testid="property-address-section">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="property-address-section">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4 group">
          <h3 className="font-semibold">Property Address</h3>
          {!isEditing && (hasAddress || showAddressUnavailable) && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="edit-address-button"
              onClick={enterEditMode}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>

        {isEditing ? (
          // Edit Mode
          <div data-testid="address-edit-mode" className="space-y-4">
            {/* Address Unavailable Checkbox - Using native input for test compatibility */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="addressUnavailable"
                name="addressUnavailable"
                checked={addressUnavailable}
                onChange={(e) => setAddressUnavailable(e.target.checked)}
                data-testid="address-unavailable-checkbox"
                className="h-4 w-4 rounded border border-primary text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
              <Label htmlFor="addressUnavailable">Address unavailable</Label>
            </div>

            {addressUnavailable ? (
              <p className="text-sm text-muted-foreground italic">
                Address not yet known
              </p>
            ) : (
              <>
                {/* Address Line 1 */}
                <div className="space-y-1">
                  <Label htmlFor="addressLine1">Address Line 1</Label>
                  <Input
                    id="addressLine1"
                    name="addressLine1"
                    value={addressLine1}
                    onChange={(e) => setAddressLine1(e.target.value)}
                    placeholder="Street address"
                    data-testid="address-line1-input"
                  />
                </div>

                {/* Address Line 2 */}
                <div className="space-y-1">
                  <Label htmlFor="addressLine2">Address Line 2</Label>
                  <Input
                    id="addressLine2"
                    name="addressLine2"
                    value={addressLine2}
                    onChange={(e) => setAddressLine2(e.target.value)}
                    placeholder="Unit, suite, etc."
                    data-testid="address-line2-input"
                  />
                </div>

                {/* Suburb */}
                <div className="space-y-1">
                  <Label htmlFor="suburb">Suburb</Label>
                  <Input
                    id="suburb"
                    name="suburb"
                    value={suburb}
                    onChange={(e) => setSuburb(e.target.value)}
                    placeholder="Suburb"
                    data-testid="suburb-input"
                  />
                </div>

                {/* State Select */}
                <div className="space-y-1">
                  <Label htmlFor="state">State</Label>
                  <Select value={state} onValueChange={setState}>
                    <SelectTrigger data-testid="state-select">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {AUSTRALIAN_STATES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Postcode */}
                <div className="space-y-1">
                  <Label htmlFor="postcode">Postcode</Label>
                  <Input
                    id="postcode"
                    name="postcode"
                    value={postcode}
                    onChange={(e) => {
                      setPostcode(e.target.value);
                      setPostcodeError(null);
                    }}
                    placeholder="0000"
                    data-testid="postcode-input"
                  />
                  {postcodeError && (
                    <p
                      className="text-sm text-destructive"
                      data-testid="postcode-error"
                    >
                      {postcodeError}
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleSave}
                disabled={saveMutation.isPending}
                data-testid="address-save-button"
              >
                {saveMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save
              </Button>
              <Button
                variant="outline"
                onClick={cancelEdit}
                disabled={saveMutation.isPending}
                data-testid="address-cancel-button"
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          // Display Mode
          <div data-testid="property-address-display">
            {showAddressUnavailable && (
              <p
                className="text-sm text-muted-foreground italic"
                data-testid="address-unavailable-message"
              >
                Address unavailable
              </p>
            )}

            {showNoAddress && !showAddressUnavailable && (
              <div className="space-y-3">
                <p
                  className="text-sm text-muted-foreground"
                  data-testid="no-address-message"
                >
                  No address recorded
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={enterEditMode}
                  data-testid="add-address-button"
                >
                  Add Address
                </Button>
              </div>
            )}

            {showAddress && (
              <p className="text-sm" data-testid="formatted-address">
                {formatAddress()}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
